import * as React from "react"
import {Check, ChevronsUpDown, User} from "lucide-react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {useEffect} from "react"
import {Button} from "@/components/ui/button"
import axios from "axios"
import {UserProfile} from "@/types/main.js"
import {countLength, formatTitle} from "@/helpers/helperFunctions.js"
import {useAuth} from "@clerk/clerk-react"
import {Skeleton} from "@/components/ui/skeleton"
import {useNavigate} from "react-router-dom"
import backendApis from "@/services/backendApis.js"
import {useKayAuth} from "@/context/AuthContext.js"
import constants from "@/constants/constants.js"
import {Avatar, AvatarFallback} from "@/components/ui/avatar.js"
import {cn} from "@/lib/utils"

type UserSwitcherProps = {
  noShow?: boolean
}

const UserSwitcher = (
  {
    noShow = false
  }: UserSwitcherProps) => {
  const navigate = useNavigate()
  const {
    isAdmin,
    ownUserId,
    setUser,
    user,
    users
  } = useKayAuth()
  const {getToken} = useAuth()
  const [open, setOpen] = React.useState(false)
  const initialSetupRef = React.useRef(true)

  useEffect(() => {
    if (countLength(users)) {
      checkAndUpdateUsers(users)
    }
  }, [users])

  const checkAndUpdateUsers = async (items: UserProfile[]) => {
    const lastUsedUserId = localStorage.getItem(constants.lastUserId)
    const userExists = lastUsedUserId ? items.find((user) => user.user_id === lastUsedUserId) : null
    if (lastUsedUserId && userExists) {
      await getUserProfile()
      updateUser(lastUsedUserId)
    } else {
      const profile = await getUserProfile()
      updateUser(profile.user_id)
    }
    initialSetupRef.current = false
  }

  const getUserProfile = async (): Promise<UserProfile> => {
    try {
      const token = await getToken()
      const response = await backendApis.userProfile({}, {
        Authorization: `Bearer ${token}`
      })
      if (response.err) {
        throw new Error(response.err)
      }
      return response.data
    } catch (error) {
      console.error(error)
    }
  }

  const updateUser = async (userId: string) => {
    if (ownUserId !== userId && userId) {
      axios.defaults.headers.common['x-on-behalf-of'] = userId
    } else {
      delete axios.defaults.headers.common['x-on-behalf-of']
    }
    const currentUser = users.find((user) => user.user_id === userId)
    if (currentUser) {
      setUser(currentUser)
    }
    localStorage.setItem(constants.lastUserId, userId)
    if (!initialSetupRef.current && user?.user_id && (userId !== user.user_id)) {
      navigate('/')
      window.location.reload()
    }
  }

  if (noShow || !isAdmin) {
    return null
  }

  if (countLength(users) === 0) {
    return (
      <Skeleton className="h-9 w-[180px] rounded-md"/>
    )
  }

  if (!users || countLength(users) === 0) {
    return (
      <Button
        className="h-9 w-[180px]"
        disabled
        variant="outline"
      >
        <User className="mr-2 h-4 w-4"/>
        No users
      </Button>
    )
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const currentUserName = formatTitle(user?.user_id || '')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className="h-9 w-[160px] md:w-[200px] justify-between"
          role="combobox"
          variant="outline"
        >
          <div className="flex items-center gap-2">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-xs">
                {getInitials(currentUserName)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">
              {currentUserName}
            </span>
          </div>
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[160px] md:w-[200px] p-0 bg-white"
      >
        <Command>
          <CommandInput placeholder="Search users..."/>
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup heading="Switch account">
              {users.map((profile) => (
                <CommandItem
                  key={profile.user_id}
                  value={profile.user_id}
                  onSelect={() => {
                    updateUser(profile.user_id)
                    setOpen(false)
                  }}
                >
                  <div className="flex items-center gap-2 w-full">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-xs">
                        {getInitials(formatTitle(profile.user_id) || "U")}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate flex-1">
                      {formatTitle(profile.user_id)}
                    </span>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        profile.user_id === user?.user_id ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default UserSwitcher
